import { CSSProperties, FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState, MouseEvent, memo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ColorPicker, ConfigProvider, Grid, Popover, theme } from "antd";
import viVN from "antd/locale/vi_VN";
import {
  ADC_COST,
  CRM_PATHS,
  GUIDLINE_PATHS,
  HRM_PATHS,
  MASTER_FORM,
  MASTER_PATHS,
  MESSAGES_PATHS,
  NEW_SHIPMENT_PATHS,
  NOTIFICATIONS_PATHS,
  REPORTS_PATH,
  ROADMAP_PATHS,
  SHIPMENT_PATHS,
  TMS_PATHS,
} from "../../../constances/Paths.constance";
import { useAuth } from "../../../hooks/useAuth";
import { RootState } from "../../../stores/Store";
import { CommonDrawer } from "../components/drawer/CommonDrawer";
import { CommonInput } from "../components/data-entry/CommonInput";
import { NotificationBell } from "../components/notification/NotificationBell";
import { FloatingChat } from "../components/chat/FloatingChat";
import { MessagesButtonHeader } from "../components/chat/MessagesButtonHeader";
import { useNotificationSocket } from "../../../hooks/useNotifications";
import { NOTIFICATION_GRID_STORAGE_KEY } from "../../../constances/NotificationsConstance";
import { setNotificationSearchText } from "../../../utils/NotificationSearch";
import { isNewShipmentApprovalDetailPath } from "./MainLayoutPathUtils";
import {
  HomeOutlined,
  FormOutlined,
  AuditOutlined,
  DollarOutlined,
  PieChartOutlined,
  BarChartOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
  CompassOutlined,
  BookOutlined,
  AppstoreOutlined,
  RightOutlined,
  MenuUnfoldOutlined,
  CloseOutlined,
  GlobalOutlined,
  BellOutlined,
  SearchOutlined,
  MessageOutlined,
  SettingOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import { DISPLAY_DATE_FORMAT, DISPLAY_DATE_TIME_FORMAT } from "../../../utils/DateUtils";
import { UserAccountMenu } from "../components/account/UserAccountMenu";

const SIDEBAR_W: number = 200;
const SIDEBAR_COLLAPSED_W: number = 44;
const TOPBAR_H: number = 46;
const LOGO_STRIP_H: number = 44;
const SHELL_BG: string = "#f4f7fb";
const SHELL_BORDER: string = "#dfe7f3";
const NAVY: string = "#001e5a";
const SIDEBAR_BACKGROUND_STORAGE_KEY = "adc_sidebar_background_color";
const SIDEBAR_TEXT_STORAGE_KEY = "adc_sidebar_text_color";
const SIDEBAR_LOGO_STORAGE_KEY = "adc_sidebar_logo_color";
const TOOLBAR_BUTTON_BACKGROUND_STORAGE_KEY = "adc_toolbar_button_background_color";
const DEFAULT_SIDEBAR_TEXT_COLOR = "#ffffff";
const DEFAULT_SIDEBAR_LOGO_COLOR = "#ffffff";

type LayoutMode = "desktop" | "tablet" | "mobile";

const TABLE_TOOLBAR_EXACT_PATHS = new Set([
  CRM_PATHS.ROOT,
  HRM_PATHS.APPLICATION_REQUESTS,
  MASTER_PATHS.APPROVAL_SETTINGS,
  MASTER_PATHS.CARRIER_PAGE,
  MASTER_PATHS.MASTER_FORM_PAGE,
  MASTER_PATHS.SHIPPER_PAGE,
  MASTER_PATHS.CNEE_PAGE,
  TMS_PATHS.VENDOR,
  SHIPMENT_PATHS.ROOT,
  SHIPMENT_PATHS.CDS,
  SHIPMENT_PATHS.CONTAINER,
  SHIPMENT_PATHS.SPEACIALIZED_LICENSE_INFOMATION,
  SHIPMENT_PATHS.FULL_LIST_CONTAINER,
  SHIPMENT_PATHS.COST,
  NEW_SHIPMENT_PATHS.ROOT,
  NEW_SHIPMENT_PATHS.CDS,
  NEW_SHIPMENT_PATHS.TRUCKING_PLAN,
  NEW_SHIPMENT_PATHS.TRUCKING,
  TMS_PATHS.TRUCKING_PLAN_DISPATCH,
  TMS_PATHS.TRUCKING,
  TMS_PATHS.TRUCKING_ACTION_SETTING,
  NEW_SHIPMENT_PATHS.BILL,
  NEW_SHIPMENT_PATHS.COST,
  NEW_SHIPMENT_PATHS.NEW_LICENSE,
  NEW_SHIPMENT_PATHS.SHIPMENT_COST_SETTINGS,
  REPORTS_PATH.RECEIVABLES_PNL,
]);

const normalizePath: (path: string) => string = (path: string) => (path === "/" ? path : path.replace(/\/+$/, ""));

const isPublicLayoutPath: (pathname: string) => boolean = (pathname: string) => {
  const path = normalizePath(pathname);
  return path === "/public" || path.startsWith("/public/");
};

const isSingleSegmentChild: (pathname: string, basePath: string) => boolean = (pathname: string, basePath: string) => {
  const base = normalizePath(basePath);
  if (!pathname.startsWith(`${base}/`)) return false;
  return (
    pathname
      .slice(base.length + 1)
      .split("/")
      .filter(Boolean).length === 1
  );
};

const usesTableToolbar: (pathname: string) => boolean = (pathname: string) => {
  const path = normalizePath(pathname);
  if (TABLE_TOOLBAR_EXACT_PATHS.has(path)) return true;
  return (
    path.startsWith(`${TMS_PATHS.VENDOR}/`) ||
    path.startsWith(`${TMS_PATHS.TRUCKING_PLAN_DISPATCH}/`) ||
    path.startsWith(`${TMS_PATHS.TRUCKING}/`) ||
    path.startsWith(`${HRM_PATHS.APPLICATION_REQUESTS}/`) ||
    path.startsWith(`${NEW_SHIPMENT_PATHS.COST}/`) ||
    isNewShipmentApprovalDetailPath(path) ||
    isSingleSegmentChild(path, MASTER_FORM.MY_APPLICATION) ||
    isSingleSegmentChild(path, MASTER_FORM.SENT_TO_ME) ||
    isSingleSegmentChild(path, NEW_SHIPMENT_PATHS.ROOT)
  );
};

const prefersInstantShellResize: (pathname: string) => boolean = (pathname: string) => {
  const path = normalizePath(pathname);
  return path.startsWith(`${NEW_SHIPMENT_PATHS.REVENUE}/`);
};

type SidebarRouteItem = {
  key: string;
  icon?: ReactNode;
  label: string;
  roles?: string[];
  requireParentRole?: boolean;
  children?: SidebarRouteItem[];
};
type SidebarDivider = { type: "divider" };
type SidebarItem = SidebarRouteItem | SidebarDivider;
type FlyoutState = { item: SidebarRouteItem; top: number } | null;

interface RecolorableLogoProps {
  color: string;
  className?: string;
  style?: CSSProperties;
}

const RecolorableLogo: FC<RecolorableLogoProps> = ({ color, className, style }) => (
  <span
    role="img"
    aria-label="ADC logo"
    className={["inline-block shrink-0", className].filter(Boolean).join(" ")}
    style={{
      backgroundColor: color,
      WebkitMaskImage: "url('/adc_logo.svg')",
      WebkitMaskPosition: "center",
      WebkitMaskRepeat: "no-repeat",
      WebkitMaskSize: "contain",
      maskImage: "url('/adc_logo.svg')",
      maskPosition: "center",
      maskRepeat: "no-repeat",
      maskSize: "contain",
      ...style,
    }}
  />
);

const menuConfig: SidebarItem[] = [
  { key: "/", icon: <HomeOutlined />, label: "HOME" },
  { key: MESSAGES_PATHS.ROOT, icon: <MessageOutlined />, label: "MESSAGES", roles: ["EMPLOYEE"] },
  { key: NOTIFICATIONS_PATHS.ROOT, icon: <BellOutlined />, label: "NOTIFICATIONS", roles: ["EMPLOYEE"] },
  { type: "divider" },
  {
    key: MASTER_FORM.MY_APPLICATION,
    icon: <FormOutlined />,
    label: "REQUEST",
    roles: ["EMPLOYEE"],
    children: [
      { key: MASTER_FORM.MY_APPLICATION, label: "ĐƠN CỦA TÔI", roles: ["EMPLOYEE"] },
      { key: MASTER_FORM.SENT_TO_ME, label: "ĐƠN GỬI TỚI TÔI", roles: ["EMPLOYEE"] },
    ],
  },
  {
    key: SHIPMENT_PATHS.ROOT,
    icon: <AuditOutlined />,
    label: "SHIPMENT",
    roles: ["EMPLOYEE", "CUSTOMER"],
    children: [
      { key: SHIPMENT_PATHS.ROOT, label: "THÔNG TIN LÔ HÀNG", roles: ["EMPLOYEE"] },
      { key: SHIPMENT_PATHS.CDS, label: "THÔNG TIN TỜ KHAI", roles: ["EMPLOYEE"] },
      { key: SHIPMENT_PATHS.CONTAINER, label: "THÔNG TIN VẬN CHUYỂN", roles: ["EMPLOYEE"] },
      { key: SHIPMENT_PATHS.SPEACIALIZED_LICENSE_INFOMATION, label: "THÔNG TIN GIẤY PHÉP", roles: ["EMPLOYEE"] },
      { key: SHIPMENT_PATHS.COST, label: "THÔNG TIN CHI PHÍ", roles: ["EMPLOYEE"] },
    ],
  },
  {
    key: NEW_SHIPMENT_PATHS.ROOT,
    icon: <GlobalOutlined />,
    label: "NEW SHIPMENT",
    roles: ["EMPLOYEE", "CUSTOMER"],
    children: [
      { key: NEW_SHIPMENT_PATHS.ROOT, label: "THÔNG TIN LÔ HÀNG", roles: ["EMPLOYEE"] },
      { key: NEW_SHIPMENT_PATHS.CDS, label: "THÔNG TIN TỜ KHAI", roles: ["EMPLOYEE"] },
      { key: NEW_SHIPMENT_PATHS.TRUCKING_PLAN, label: "KẾ HOẠCH VẬN CHUYỂN", roles: ["EMPLOYEE"] },
      { key: NEW_SHIPMENT_PATHS.TRUCKING, label: "THÔNG TIN VẬN CHUYỂN", roles: ["EMPLOYEE"] },
      { key: NEW_SHIPMENT_PATHS.BILL, label: "THÔNG TIN VẬN ĐƠN", roles: ["EMPLOYEE"] },
      { key: NEW_SHIPMENT_PATHS.NEW_LICENSE, label: "THÔNG TIN GIẤY PHÉP", roles: ["EMPLOYEE"] },
      { key: NEW_SHIPMENT_PATHS.COST, label: "THÔNG TIN CHI PHÍ", roles: ["EMPLOYEE"] },
      { key: MASTER_PATHS.SHIPPER_PAGE, label: "THÔNG TIN SHIPPER", roles: ["EMPLOYEE"] },
      { key: MASTER_PATHS.CNEE_PAGE, label: "THÔNG TIN CONSIGNEE", roles: ["EMPLOYEE"] },
      { key: MASTER_PATHS.CARRIER_PAGE, label: "THÔNG TIN HÃNG TÀU", roles: ["EMPLOYEE"] },
      { key: MASTER_PATHS.LOCATION_ADDRESS, label: "QUẢN LÝ ĐỊA ĐIỂM", roles: ["EMPLOYEE"] },
      { key: NEW_SHIPMENT_PATHS.MASTER_CDS_ACTION, label: "CÀI ĐẶT TỜ KHAI", roles: ["MASTER_CDS_ACTION_VIEW"] },
      {
        key: NEW_SHIPMENT_PATHS.SHIPMENT_COST_SETTINGS,
        label: "CÀI ĐẶT CHI PHÍ",
        roles: ["COST_FORM_SETTING", "REVENUE_APPROVAL_SETTING"],
      },
    ],
  },
  {
    key: TMS_PATHS.ROOT,
    icon: <TruckOutlined />,
    label: "TMS",
    roles: ["TMS_DISPATCH", "MASTER_TRUCKING_ACTION", "TMS_COST_APPROVAL_SETTING"],
    requireParentRole: true,
    children: [
      { key: TMS_PATHS.ROOT, label: "DASHBOARD", roles: ["TMS_DISPATCH"] },
      { key: TMS_PATHS.DIEU_HANH_TRUCKING, label: "ĐIỀU PHỐI VẬN CHUYỂN", roles: ["TMS_DISPATCH"] },
      { key: TMS_PATHS.COST_OVERVIEW, label: "CHI PHÍ VẬN TẢI", roles: ["TMS_DISPATCH"] },
      { key: TMS_PATHS.VENDOR, label: "QUẢN LÝ VENDOR", roles: ["TMS_DISPATCH"] },
      { key: TMS_PATHS.FLEET_MANAGEMENT, label: "QUẢN LÝ ĐỘI XE", roles: ["TMS_DISPATCH"] },
      { key: TMS_PATHS.TRUCKING_ACTION_SETTING, label: "CÀI ĐẶT VẬN CHUYỂN", roles: ["MASTER_TRUCKING_ACTION"] },
      { key: TMS_PATHS.COST_APPROVAL_SETTING, label: "CẤU HÌNH DUYỆT CHI PHÍ", roles: ["TMS_COST_APPROVAL_SETTING"] },
    ],
  },
  {
    key: ADC_COST.ROOT,
    icon: <DollarOutlined />,
    label: "ADC COSTS",
    roles: ["EMPLOYEE"],
  },
  {
    key: REPORTS_PATH.ROOT,
    icon: <PieChartOutlined />,
    label: "REPORTS",
    roles: ["EMPLOYEE", "REPORT_VIEW"],
    children: [
      { key: REPORTS_PATH.ROOT, icon: <PieChartOutlined />, label: "OVERVIEW", roles: ["EMPLOYEE", "REPORT_VIEW"] },
      { key: REPORTS_PATH.RECEIVABLES_PNL, icon: <BarChartOutlined />, label: "PROFIT & LOSS", roles: ["EMPLOYEE", "REPORT_VIEW"] },
    ],
  },
  { type: "divider" },
  {
    key: HRM_PATHS.ROOT,
    icon: <UserAddOutlined />,
    label: "HRM",
    roles: ["EMPLOYEE"],
  },
  { key: CRM_PATHS.ROOT, icon: <UsergroupAddOutlined />, label: "CUSTOMER", roles: ["MASTER_CUSTOMER_VIEW"] },
  { key: ROADMAP_PATHS.ROOT, icon: <CompassOutlined />, label: "ROADMAP", roles: ["EMPLOYEE"] },
  { key: GUIDLINE_PATHS.ROOT, icon: <BookOutlined />, label: "USER GUIDE", roles: ["EMPLOYEE"] },
  {
    key: GUIDLINE_PATHS.ADMIN,
    icon: <AppstoreOutlined />,
    label: "POST ADMIN",
    roles: ["POST_VIEW_ALL", "POST_CATEGORY_VIEW_ALL", "POST_TAG_VIEW_ALL"],
  },
];

const isDivider: (item: SidebarItem) => item is SidebarDivider = (item: SidebarItem): item is SidebarDivider =>
  "type" in item && item.type === "divider";

const canView: (roles: string[], itemRoles?: string[]) => boolean = (roles: string[], itemRoles?: string[]) =>
  !itemRoles || itemRoles.some((r) => roles.includes(r));

const filterMenuConfig: (items: SidebarItem[], roles: string[]) => SidebarItem[] = (items: SidebarItem[], roles: string[]): SidebarItem[] => {
  const out: SidebarItem[] = [];
  items.forEach((item) => {
    if (isDivider(item)) {
      if (out.length && !isDivider(out[out.length - 1])) out.push(item);
      return;
    }
    if (item.requireParentRole && !canView(roles, item.roles)) return;
    const children = item.children?.filter((c) => canView(roles, c.roles)) ?? [];
    if (canView(roles, item.roles) || children.length) out.push({ ...item, children: children.length ? children : undefined });
  });
  while (out.length && isDivider(out[out.length - 1])) out.pop();
  return out;
};

const collectPathKeys: (items: SidebarItem[]) => string[] = (items: SidebarItem[]): string[] =>
  items.flatMap((item) => (isDivider(item) ? [] : [item.key, ...(item.children ? collectPathKeys(item.children) : [])]));

const getSelectedKeyAlias: (pathname: string) => string | undefined = (pathname: string): string | undefined => {
  const path = normalizePath(pathname);
  if (path === NEW_SHIPMENT_PATHS.REVENUE || path.startsWith(`${NEW_SHIPMENT_PATHS.REVENUE}/`)) return NEW_SHIPMENT_PATHS.COST;
  if (/^\/new-shipments\/[^/]+\/shipment-cost(\/|$)/.test(path)) return NEW_SHIPMENT_PATHS.COST;
  return undefined;
};

const getSelectedKey = (pathname: string, search: string, items: SidebarItem[]): string => {
  const alias = getSelectedKeyAlias(pathname);
  if (alias && collectPathKeys(items).includes(alias)) return alias;

  const keys = collectPathKeys(items);
  const path = normalizePath(pathname);
  const exactLocationKey = `${path}${search}`;
  if (keys.includes(exactLocationKey)) return exactLocationKey;

  if (path === TMS_PATHS.TRUCKING_PLAN_DISPATCH || path.startsWith(`${TMS_PATHS.TRUCKING_PLAN_DISPATCH}/`)) {
    const isCostPage = path.includes("/costs/") || new URLSearchParams(search).get("tab") === "cost";
    const tmsKey = isCostPage ? TMS_PATHS.COST_OVERVIEW : TMS_PATHS.DIEU_HANH_TRUCKING;
    if (keys.includes(tmsKey)) return tmsKey;
  }

  return (
    keys
      .filter((key) => {
        const menuPath = normalizePath(key.split("?")[0]);
        return menuPath.startsWith("/") && (path === menuPath || path.startsWith(`${menuPath}/`));
      })
      .sort((a, b) => b.split("?")[0].length - a.split("?")[0].length)[0] ?? pathname
  );
};

const getParentKeys: (targetKey: string, items: SidebarItem[]) => string[] = (targetKey: string, items: SidebarItem[]): string[] => {
  const visit = (list: SidebarItem[], parents: string[]): string[] | null => {
    for (const item of list) {
      if (isDivider(item)) continue;
      if (item.key === targetKey) return parents;
      const found = item.children ? visit(item.children, [...parents, item.key]) : null;
      if (found) return found;
    }
    return null;
  };
  return visit(items, []) ?? [];
};

const useLayoutMode: () => { mode: LayoutMode; ready: boolean } = () => {
  const screens = Grid.useBreakpoint();
  const [mode, setMode] = useState<LayoutMode>("desktop");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const hasKeys = Object.keys(screens).length > 0;
    if (!hasKeys) return;

    let next: LayoutMode;
    if (screens.lg) next = "desktop";
    else if (screens.md) next = "tablet";
    else next = "mobile";

    setMode(next);
    setReady(true);
  }, [screens]);

  return { mode, ready };
};

interface MenuItemProps {
  item: SidebarRouteItem;
  selectedKey: string;
  collapsed: boolean;
  isDefaultOpen: boolean;
  navigate: (key: string) => void;
  onNavigate?: () => void;
  onFlyoutEnter?: (e: MouseEvent<HTMLDivElement>, item: SidebarRouteItem) => void;
  onFlyoutLeave?: () => void;
  textColor: string;
}

const SidebarMenuItem: FC<MenuItemProps> = ({
  item,
  selectedKey,
  collapsed,
  isDefaultOpen,
  navigate,
  onNavigate,
  onFlyoutEnter,
  onFlyoutLeave,
  textColor,
}) => {
  const [open, setOpen] = useState(isDefaultOpen);
  const hasChildren = !!item.children?.length;
  const isActive = selectedKey === item.key;
  const isParentActive = hasChildren && item.children!.some((c) => c.key === selectedKey);

  useEffect(() => {
    if (isDefaultOpen) setOpen(true);
  }, [isDefaultOpen]);

  if (collapsed) {
    return (
      <div
        className="relative mx-1 my-0.5"
        onMouseEnter={hasChildren ? (e) => onFlyoutEnter?.(e, item) : undefined}
        onMouseLeave={hasChildren ? onFlyoutLeave : undefined}
      >
        <button
          type="button"
          onClick={
            !hasChildren
              ? () => {
                  navigate(item.key);
                  onNavigate?.();
                }
              : undefined
          }
          title={item.label}
          className={[
            "w-full flex items-center justify-center h-9 transition-colors duration-150 rounded-none border-none outline-none cursor-pointer",
            isActive || isParentActive ? "bg-white/20 text-white" : "text-white/90 hover:bg-white/10 hover:text-white",
          ].join(" ")}
          style={{ color: textColor }}
        >
          <span className="text-[14px]">{item.icon}</span>
        </button>
      </div>
    );
  }

  if (hasChildren) {
    return (
      <div className="my-0.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={[
            "w-[calc(100%-12px)] mx-1.5 flex items-center gap-2 h-[34px] px-2.5 border-none outline-none cursor-pointer rounded-none",
            "text-[11.5px] font-bold tracking-normal transition-colors duration-150",
            isParentActive ? "bg-white/20 text-white" : "text-white/90 hover:text-white hover:bg-white/10",
          ].join(" ")}
          style={{ color: textColor }}
        >
          <span className="text-[14px] min-w-[14px] flex-shrink-0">{item.icon}</span>
          <span className="flex-1 text-left truncate">{item.label}</span>
          <RightOutlined className={["text-[9px] opacity-70 transition-transform duration-200 flex-shrink-0", open ? "rotate-90" : ""].join(" ")} />
        </button>
        {open && (
          <div className="mx-1.5 mt-0.5 mb-1 bg-black/20 overflow-hidden">
            {item.children!.map((child) => (
              <button
                type="button"
                key={child.key}
                onClick={() => {
                  navigate(child.key);
                  onNavigate?.();
                }}
                className={[
                  "w-full h-[30px] flex items-center text-left border-none outline-none cursor-pointer rounded-none",
                  "text-[11px] font-semibold tracking-normal transition-colors duration-150",
                  selectedKey === child.key ? "text-white bg-white/20" : "text-white/80 hover:text-white hover:bg-white/10",
                ].join(" ")}
                style={{ paddingLeft: 28, color: textColor }}
              >
                {child.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="my-0.5 mx-1.5">
      <button
        type="button"
        onClick={() => {
          navigate(item.key);
          onNavigate?.();
        }}
        className={[
          "w-full flex items-center gap-2 h-[34px] px-2.5 border-none outline-none cursor-pointer rounded-none",
          "text-[11.5px] font-bold tracking-normal transition-colors duration-150",
          isActive ? "bg-white/20 text-white" : "text-white/90 hover:text-white hover:bg-white/10",
        ].join(" ")}
        style={{ color: textColor }}
      >
        <span className="text-[14px] min-w-[14px] flex-shrink-0">{item.icon}</span>
        <span className="truncate">{item.label}</span>
      </button>
    </div>
  );
};

interface SidebarProps {
  collapsed: boolean;
  topOffset?: number;
  inline?: boolean;
  onNavigate?: () => void;
  backgroundColor: string;
  textColor: string;
  onBackgroundColorChange: (color: string) => void;
  onTextColorChange: (color: string) => void;
  logoColor: string;
  onLogoColorChange: (color: string) => void;
  toolbarButtonBackgroundColor: string;
  onToolbarButtonBackgroundColorChange: (color: string) => void;
  onResetColors: () => void;
}

const Sidebar: FC<SidebarProps> = ({
  collapsed,
  topOffset = TOPBAR_H,
  inline = false,
  onNavigate,
  backgroundColor,
  textColor,
  onBackgroundColorChange,
  onTextColorChange,
  logoColor,
  onLogoColorChange,
  toolbarButtonBackgroundColor,
  onToolbarButtonBackgroundColorChange,
  onResetColors,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { roles, permissions } = useAuth();
  const grantedKeys = useMemo(() => [...roles, ...(permissions ?? [])], [roles, permissions]);

  const [flyout, setFlyout] = useState<FlyoutState>(null);
  const flyoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visibleMenu = useMemo(() => filterMenuConfig(menuConfig, grantedKeys), [grantedKeys]);
  const selectedKey = getSelectedKey(location.pathname, location.search, visibleMenu);
  const parentKeys = useMemo(() => getParentKeys(selectedKey, visibleMenu), [selectedKey, visibleMenu]);

  const showFlyout = useCallback((item: SidebarRouteItem, top: number) => {
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
    setFlyout({ item, top });
  }, []);

  const hideFlyout = useCallback(() => {
    flyoutTimer.current = setTimeout(() => setFlyout(null), 80);
  }, []);

  const flyoutEnter = useCallback(
    (e: MouseEvent<HTMLDivElement>, item: SidebarRouteItem) => {
      showFlyout(item, e.currentTarget.getBoundingClientRect().top);
    },
    [showFlyout],
  );

  useEffect(() => {
    setFlyout(null);
  }, [collapsed]);
  useEffect(
    () => () => {
      if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
    },
    [],
  );

  return (
    <div
      className={[
        "flex flex-col overflow-hidden",
        inline ? "relative w-full h-full" : "fixed left-0 z-40 shadow-[2px_0_20px_rgba(0,0,0,0.12)] transition-[width] duration-[250ms] ease-in-out",
      ].join(" ")}
      style={
        inline
          ? { backgroundColor }
          : {
              top: topOffset,
              width: collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W,
              height: `calc(100vh - ${topOffset}px)`,
              backgroundColor,
            }
      }
    >
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-1.5" style={{ scrollbarWidth: "none" }}>
        {visibleMenu.map((item, idx) => {
          if (isDivider(item)) return <div key={`div-${idx}`} className="mx-3.5 my-1.5 border-t border-slate-400/10" />;
          return (
            <SidebarMenuItem
              key={item.key}
              item={item}
              selectedKey={selectedKey}
              collapsed={!inline && collapsed}
              isDefaultOpen={parentKeys.includes(item.key)}
              navigate={navigate}
              onNavigate={onNavigate}
              onFlyoutEnter={!inline && collapsed ? flyoutEnter : undefined}
              onFlyoutLeave={!inline && collapsed ? hideFlyout : undefined}
              textColor={textColor}
            />
          );
        })}
      </div>

      <div className="border-t border-white/10 p-1.5">
        <Popover
          trigger="click"
          placement={collapsed && !inline ? "rightBottom" : "top"}
          content={
            <div className="w-[190px] space-y-3 py-1">
              <div className="flex items-center justify-between gap-3 text-xs text-slate-700">
                <span>Màu nền sidebar</span>
                <ColorPicker value={backgroundColor} onChange={(_, css) => onBackgroundColorChange(css)} showText />
              </div>
              <div className="flex items-center justify-between gap-3 text-xs text-slate-700">
                <span>Màu chữ sidebar</span>
                <ColorPicker value={textColor} onChange={(_, css) => onTextColorChange(css)} showText />
              </div>
              <div className="flex items-center justify-between gap-3 text-xs text-slate-700">
                <span>Màu logo</span>
                <ColorPicker value={logoColor} onChange={(_, css) => onLogoColorChange(css)} showText />
              </div>
              <div className="flex items-center justify-between gap-3 text-xs text-slate-700">
                <span>Màu nút toolbar</span>
                <ColorPicker value={toolbarButtonBackgroundColor} onChange={(_, css) => onToolbarButtonBackgroundColorChange(css)} showText />
              </div>
              <button
                type="button"
                onClick={onResetColors}
                className="w-full border-t border-slate-200 pt-2 text-left text-xs font-medium text-slate-600 transition-colors hover:text-blue-600 border-none outline-none cursor-pointer rounded-none bg-transparent"
              >
                Khôi phục màu mặc định
              </button>
            </div>
          }
        >
          <button
            type="button"
            title="Tùy chỉnh màu sidebar"
            className="flex h-8 w-full items-center justify-center gap-2 text-xs font-semibold transition-colors hover:bg-white/10 border-none outline-none cursor-pointer rounded-none"
            style={{ color: textColor }}
          >
            <SettingOutlined />
            {(!collapsed || inline) && <span>Tùy chỉnh màu</span>}
          </button>
        </Popover>
      </div>

      {flyout && !inline && collapsed && (
        <div
          className="fixed z-50 py-1 border border-slate-700/40 shadow-xl rounded-none"
          style={{ left: SIDEBAR_COLLAPSED_W + 6, top: flyout.top, minWidth: 140, maxWidth: 196, backgroundColor }}
          onMouseEnter={() => {
            if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
          }}
          onMouseLeave={hideFlyout}
        >
          {flyout.item.children!.map((child) => (
            <button
              type="button"
              key={child.key}
              onClick={() => {
                navigate(child.key);
                onNavigate?.();
                setFlyout(null);
              }}
              className={[
                "w-full text-left px-2.5 h-7 flex items-center truncate border-none outline-none cursor-pointer rounded-none",
                "text-[11.5px] font-semibold tracking-normal transition-colors duration-150",
                selectedKey === child.key ? "text-white bg-white/20" : "text-white/80 hover:text-white hover:bg-white/10",
              ].join(" ")}
              style={{ color: textColor }}
            >
              {child.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MemoOutlet: FC = memo(() => <Outlet />);

const antdTheme = {
  algorithm: theme.defaultAlgorithm,
  token: { colorPrimary: NAVY, borderRadius: 0, fontSize: 12 },
};

const appLocale = {
  ...viVN,
  DatePicker: {
    ...viVN.DatePicker!,
    lang: {
      ...viVN.DatePicker!.lang,
      fieldDateFormat: DISPLAY_DATE_FORMAT,
      fieldDateTimeFormat: DISPLAY_DATE_TIME_FORMAT,
    },
  },
};

const MainLayout: FC = () => {
  const location = useLocation();
  const { userId } = useSelector((state: RootState) => state.auth);
  useNotificationSocket(!!userId);
  const { mode, ready } = useLayoutMode();
  const isPublicPath = useMemo(() => isPublicLayoutPath(location.pathname), [location.pathname]);
  const isNotificationsPath = useMemo(() => normalizePath(location.pathname) === NOTIFICATIONS_PATHS.ROOT, [location.pathname]);
  const [notificationKeyword, setNotificationKeyword] = useState(() => {
    try {
      return localStorage.getItem(`${NOTIFICATION_GRID_STORAGE_KEY}.search`) ?? "";
    } catch {
      return "";
    }
  });

  const isDesktop = mode === "desktop";
  const isTablet = mode === "tablet";
  const isMobile = mode === "mobile";

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("adc_sidebar_collapsed") === "true";
    } catch {
      return false;
    }
  });

  const [sidebarBackgroundColor, setSidebarBackgroundColor] = useState<string>(() => {
    try {
      return localStorage.getItem(SIDEBAR_BACKGROUND_STORAGE_KEY) || NAVY;
    } catch {
      return NAVY;
    }
  });
  const [sidebarTextColor, setSidebarTextColor] = useState<string>(() => {
    try {
      return localStorage.getItem(SIDEBAR_TEXT_STORAGE_KEY) || DEFAULT_SIDEBAR_TEXT_COLOR;
    } catch {
      return DEFAULT_SIDEBAR_TEXT_COLOR;
    }
  });
  const [sidebarLogoColor, setSidebarLogoColor] = useState<string>(() => {
    try {
      return localStorage.getItem(SIDEBAR_LOGO_STORAGE_KEY) || DEFAULT_SIDEBAR_LOGO_COLOR;
    } catch {
      return DEFAULT_SIDEBAR_LOGO_COLOR;
    }
  });
  const [toolbarButtonBackgroundColor, setToolbarButtonBackgroundColor] = useState<string>(() => {
    try {
      return localStorage.getItem(TOOLBAR_BUTTON_BACKGROUND_STORAGE_KEY) || NAVY;
    } catch {
      return NAVY;
    }
  });
  const skipSidebarColorStorageWrite = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem("adc_sidebar_collapsed", String(collapsed));
    } catch {}
  }, [collapsed]);

  useEffect(() => {
    if (skipSidebarColorStorageWrite.current) {
      skipSidebarColorStorageWrite.current = false;
      return;
    }
    try {
      localStorage.setItem(SIDEBAR_BACKGROUND_STORAGE_KEY, sidebarBackgroundColor);
      localStorage.setItem(SIDEBAR_TEXT_STORAGE_KEY, sidebarTextColor);
      localStorage.setItem(SIDEBAR_LOGO_STORAGE_KEY, sidebarLogoColor);
      localStorage.setItem(TOOLBAR_BUTTON_BACKGROUND_STORAGE_KEY, toolbarButtonBackgroundColor);
    } catch {}
  }, [sidebarBackgroundColor, sidebarTextColor, sidebarLogoColor, toolbarButtonBackgroundColor]);

  const resetSidebarColors = useCallback(() => {
    skipSidebarColorStorageWrite.current =
      sidebarBackgroundColor !== NAVY ||
      sidebarTextColor !== DEFAULT_SIDEBAR_TEXT_COLOR ||
      sidebarLogoColor !== DEFAULT_SIDEBAR_LOGO_COLOR ||
      toolbarButtonBackgroundColor !== NAVY;
    try {
      localStorage.removeItem(SIDEBAR_BACKGROUND_STORAGE_KEY);
      localStorage.removeItem(SIDEBAR_TEXT_STORAGE_KEY);
      localStorage.removeItem(SIDEBAR_LOGO_STORAGE_KEY);
      localStorage.removeItem(TOOLBAR_BUTTON_BACKGROUND_STORAGE_KEY);
    } catch {}
    setSidebarBackgroundColor(NAVY);
    setSidebarTextColor(DEFAULT_SIDEBAR_TEXT_COLOR);
    setSidebarLogoColor(DEFAULT_SIDEBAR_LOGO_COLOR);
    setToolbarButtonBackgroundColor(NAVY);
  }, [sidebarBackgroundColor, sidebarTextColor, sidebarLogoColor, toolbarButtonBackgroundColor]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const onNotificationKeywordChange = (keyword: string) => {
    const normalizedKeyword = keyword.trimStart();
    setNotificationKeyword(normalizedKeyword);
    setNotificationSearchText(normalizedKeyword);
  };


  const hasToolbar = useMemo(() => !isPublicPath && usesTableToolbar(location.pathname), [isPublicPath, location.pathname]);
  const instantShellResize = useMemo(() => prefersInstantShellResize(location.pathname), [location.pathname]);

  useEffect(() => {
    if (!ready) return;
    setDrawerOpen(false);
    if (isTablet) setCollapsed(true);
  }, [ready, isDesktop, isTablet, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const sidebarW = isMobile ? 0 : isTablet ? SIDEBAR_COLLAPSED_W : collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W;

  const topbarH = hasToolbar ? TOPBAR_H : 0;
  const sidebarTop = hasToolbar ? topbarH : LOGO_STRIP_H;

  const toolbarTopOffset = isMobile && hasToolbar ? LOGO_STRIP_H : 0;

  const mainPaddingTop = hasToolbar ? (isMobile ? LOGO_STRIP_H + topbarH : topbarH) : LOGO_STRIP_H;

  // Cập nhật CSS Variable trực tiếp để tránh Outlet bị re-render do thẻ main thay đổi props
  useEffect(() => {
    document.documentElement.style.setProperty("--sidebar-w", `${sidebarW}px`);
    document.documentElement.style.setProperty("--main-padding-top", `${mainPaddingTop}px`);
  }, [sidebarW, mainPaddingTop]);

  const cssVars = {
    "--app-sidebar-w": `${sidebarW}px`,
    "--app-topbar-h": `${topbarH}px`,
    "--app-toolbar-top": `${toolbarTopOffset}px`,
    "--app-shell-bg": SHELL_BG,
    "--app-shell-border": SHELL_BORDER,
    "--app-navy": sidebarBackgroundColor,
    "--app-toolbar-bg": sidebarBackgroundColor,
    "--app-toolbar-text": sidebarTextColor,
    "--app-toolbar-button-bg": toolbarButtonBackgroundColor,
    "--app-toolbar-button-text": sidebarTextColor,
  } as CSSProperties;

  if (isPublicPath) {
    return (
      <ConfigProvider theme={antdTheme} locale={appLocale}>
        <Outlet />
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={antdTheme} locale={appLocale}>
      <div style={cssVars}>
        <div
          className="fixed top-0 left-0 z-[51] flex items-center overflow-hidden"
          style={{
            width: isMobile ? "100%" : sidebarW,
            height: hasToolbar ? TOPBAR_H : LOGO_STRIP_H,
            transition: instantShellResize ? "none" : "width 0.25s ease",
            backgroundColor: sidebarBackgroundColor,
            color: sidebarTextColor,
          }}
        >
          {isMobile && (
            <div className="flex items-center gap-2.5 px-3 w-full">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="flex items-center justify-center text-slate-200 bg-transparent border-none outline-none cursor-pointer p-1 -ml-1 shrink-0 rounded-none"
                style={{ color: sidebarTextColor }}
              >
                <MenuUnfoldOutlined style={{ fontSize: 16 }} />
              </button>
              <RecolorableLogo color={sidebarLogoColor} className="h-5 w-5" />
              <span className="text-xs font-extrabold tracking-[0.08em] whitespace-nowrap" style={{ color: sidebarTextColor }}>
                ASIA DRAGON
              </span>
              <div className="ml-auto flex items-center gap-2">
                <MessagesButtonHeader tone="light" />
                <NotificationBell tone="light" />
                <UserAccountMenu triggerClassName="h-[26px] w-[26px] rounded-full bg-white/10 transition-colors hover:bg-white/20" />
              </div>
            </div>
          )}

          {isTablet && (
            <div className="flex items-center justify-center w-full">
              <RecolorableLogo color={sidebarLogoColor} className="h-[22px] w-[22px]" />
            </div>
          )}

          {isDesktop && (
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              title={collapsed ? "Mở rộng sidebar" : "Thu gọn sidebar"}
              className={[
                "flex items-center gap-2 bg-transparent border-none outline-none cursor-pointer group min-w-0 h-full rounded-none",
                collapsed ? "justify-center w-full" : "px-3",
              ].join(" ")}
            >
              <RecolorableLogo
                color={sidebarLogoColor}
                style={{ width: collapsed ? 26 : 24, height: collapsed ? 26 : 24 }}
                className="transition-transform duration-150 group-hover:scale-110"
              />
              {!collapsed && (
                <span className="text-xs font-extrabold tracking-[0.08em] whitespace-nowrap transition-colors duration-150" style={{ color: sidebarTextColor }}>
                  ASIA DRAGON
                </span>
              )}
            </button>
          )}
        </div>

        {!hasToolbar && (
          <div
            className="fixed z-[50] flex items-center border-b border-white/[0.08]"
            style={{
              top: 0,
              right: 0,
              height: LOGO_STRIP_H,
              paddingRight: 12,
              left: isMobile ? 0 : sidebarW,
              transition: instantShellResize ? "none" : "left 0.25s ease",
              backgroundColor: sidebarBackgroundColor,
              color: sidebarTextColor,
            }}
          >
            {isNotificationsPath && (
              <div className="relative flex w-[220px] max-w-[calc(100vw-120px)] items-center sm:w-[280px]">
                <SearchOutlined className="pointer-events-none absolute left-2.5 z-10 text-[11px]" style={{ color: sidebarTextColor }} />
                <CommonInput
                  allowClear
                  variant="borderless"
                  value={notificationKeyword}
                  onChange={(event) => onNotificationKeywordChange(event.target.value)}
                  placeholder="Tìm kiếm"
                  className={[
                    "!h-[26px] !w-full !rounded-none",
                    "!border !border-white/10 !bg-white/[0.1]",
                    "!pl-7 !pr-3 !text-[11.5px] !text-[var(--app-toolbar-text,#fff)]",
                    "placeholder:!text-[var(--app-toolbar-text,#fff)]",
                    "!outline-none transition-colors duration-150",
                    "focus:!border-white/25 focus:!bg-white/[0.12]",
                    "[&_.ant-input]:!bg-transparent [&_.ant-input]:!text-[var(--app-toolbar-text,#fff)]",
                    "[&_.ant-input::placeholder]:!text-[var(--app-toolbar-text,#fff)]",
                    "[&_.ant-input-clear-icon]:!text-[var(--app-toolbar-text,#fff)]",
                  ].join(" ")}
                />
              </div>
            )}

            <div className="ml-auto flex items-center gap-3">
              <MessagesButtonHeader tone="light" />
              <NotificationBell tone="light" />
              <UserAccountMenu triggerClassName="h-[28px] w-[28px] rounded-full bg-white/10 transition-all hover:bg-white/20 hover:ring-2 hover:ring-white/20" />
            </div>
          </div>
        )}

        {!isMobile && (
          <Sidebar
            collapsed={isTablet ? true : collapsed}
            topOffset={sidebarTop}
            backgroundColor={sidebarBackgroundColor}
            textColor={sidebarTextColor}
            onBackgroundColorChange={setSidebarBackgroundColor}
            onTextColorChange={setSidebarTextColor}
            logoColor={sidebarLogoColor}
            onLogoColorChange={setSidebarLogoColor}
            toolbarButtonBackgroundColor={toolbarButtonBackgroundColor}
            onToolbarButtonBackgroundColorChange={setToolbarButtonBackgroundColor}
            onResetColors={resetSidebarColors}
          />
        )}

        <CommonDrawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={260}
          closeIcon={<CloseOutlined className="text-slate-200" />}
          title={<span className="text-slate-200 font-bold text-[13px]">ASIA DRAGON</span>}
          styles={{
            header: { background: sidebarBackgroundColor, color: sidebarTextColor, borderBottom: "1px solid rgba(148,163,184,0.12)" },
            body: { padding: 0, background: sidebarBackgroundColor },
          }}
        >
          <Sidebar
            collapsed={false}
            inline
            onNavigate={() => setDrawerOpen(false)}
            backgroundColor={sidebarBackgroundColor}
            textColor={sidebarTextColor}
            onBackgroundColorChange={setSidebarBackgroundColor}
            onTextColorChange={setSidebarTextColor}
            logoColor={sidebarLogoColor}
            onLogoColorChange={setSidebarLogoColor}
            toolbarButtonBackgroundColor={toolbarButtonBackgroundColor}
            onToolbarButtonBackgroundColorChange={setToolbarButtonBackgroundColor}
            onResetColors={resetSidebarColors}
          />
        </CommonDrawer>

        <main
          className="flex-1 min-w-0 h-screen max-h-screen overflow-hidden flex flex-col"
          style={{
            marginLeft: "var(--sidebar-w)",
            paddingTop: "var(--main-padding-top)",
            transition: instantShellResize ? "none" : "margin-left 0.1s ease",
          }}
        >
          <MemoOutlet />
        </main>

        <FloatingChat />
      </div>
    </ConfigProvider>
  );
};

export default MainLayout;
